'use client';

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface CursorTooltipProps {
  children: React.ReactNode;
  tooltipText: string;
  backgroundColor?: string;
  textColor?: string;
}

export default function CursorTooltip({ children, tooltipText, backgroundColor = '#000000', textColor = '#ffffff' }: CursorTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const isTouchInteractionRef = useRef(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect touch device
  useEffect(() => {
    const checkTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(checkTouch);
  }, []);

  // Always track cursor position globally (independent of tooltip visibility)
  // This ensures we have cursor position available even when tooltip isn't visible
  useEffect(() => {
    const handleAlwaysTrackMouse = (e: MouseEvent) => {
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
    };
    
    // Start tracking immediately on mount
    window.addEventListener('mousemove', handleAlwaysTrackMouse, { passive: true });
    
    return () => {
      window.removeEventListener('mousemove', handleAlwaysTrackMouse);
    };
  }, []); // Runs on every component mount (page navigation)

  useEffect(() => {
    const calculateTooltipPosition = (cursorX: number, cursorY: number, tooltipWidth: number): { x: number; y: number } => {
      const pageWidth = window.innerWidth;
      const offset = 6;
      
      // Try positioning to the right first
      let x = cursorX + offset;
      
      // Check if tooltip would overflow the right edge
      if (x + tooltipWidth > pageWidth) {
        // Position to the left instead
        x = cursorX - tooltipWidth - offset;
        // Ensure it doesn't overflow the left edge
        if (x < 0) {
          x = offset;
        }
      }
      
      const y = cursorY + offset;
      return { x, y };
    };


    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Track last mouse position
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
      
      // Directly update tooltip position via style - no state, instant update
      if (tooltipRef.current && isVisible) {
        const tooltipWidth = tooltipRef.current.offsetWidth || 0;
        const { x, y } = calculateTooltipPosition(e.clientX, e.clientY, tooltipWidth);
        
        tooltipRef.current.style.left = `${x}px`;
        tooltipRef.current.style.top = `${y}px`;
        tooltipRef.current.style.transform = 'none';
        setTooltipPosition({ x, y });
      }
    };

    const handleContainerMouseMove = (e: MouseEvent) => {
      // Track mouse position when over the container
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
      
      // If tooltip is visible, update its position immediately
      if (tooltipRef.current && isVisible) {
        const tooltipWidth = tooltipRef.current.offsetWidth || 0;
        const { x, y } = calculateTooltipPosition(e.clientX, e.clientY, tooltipWidth);
        tooltipRef.current.style.left = `${x}px`;
        tooltipRef.current.style.top = `${y}px`;
        tooltipRef.current.style.transform = 'none';
        setTooltipPosition({ x, y });
      }
    };

    // Function to get current mouse position and update tooltip
    const updateTooltipWithCurrentPosition = () => {
      if (!tooltipRef.current || !isVisible) return;
      
      // Use last known position from global mousemove or container mousemove
      if (lastMousePositionRef.current) {
        updateTooltipPosition(
          lastMousePositionRef.current.x,
          lastMousePositionRef.current.y
        );
      }
    };

    const updateTooltipPosition = (clientX: number, clientY: number) => {
      if (!tooltipRef.current) return;
      
      const tooltipWidth = tooltipRef.current.offsetWidth || 0;
      const { x, y } = calculateTooltipPosition(clientX, clientY, tooltipWidth);
      tooltipRef.current.style.left = `${x}px`;
      tooltipRef.current.style.top = `${y}px`;
      tooltipRef.current.style.transform = 'none';
      setTooltipPosition({ x, y });
    };

    const handleMouseEnter = () => {
      // On touch devices, skip mouse events (tooltip shown via touch)
      if (isTouchDevice) return;
      
      // Always show the tooltip
      setIsVisible(true);
      
      // Get position - use last known if available, otherwise estimate from container
      let cursorX: number;
      let cursorY: number;
      
      if (lastMousePositionRef.current) {
        // We have a known position - use it
        cursorX = lastMousePositionRef.current.x;
        cursorY = lastMousePositionRef.current.y;
      } else if (containerRef.current) {
        // No known position - estimate from container position
        // When scrolling, cursor is likely near the top-center of the container
        const rect = containerRef.current.getBoundingClientRect();
        cursorX = rect.left + rect.width / 2;
        cursorY = rect.top + 50; // Estimate cursor is near top of container
        // Store this as the last known position
        lastMousePositionRef.current = { x: cursorX, y: cursorY };
      } else {
        // Fallback
        cursorX = 0;
        cursorY = 0;
      }
      
      // Set initial position
      const offset = 6;
      const initialX = cursorX + offset;
      const initialY = cursorY + offset;
      setTooltipPosition({ x: initialX, y: initialY });
      
      // Update with proper width calculation after render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (tooltipRef.current && lastMousePositionRef.current) {
            updateTooltipPosition(
              lastMousePositionRef.current.x,
              lastMousePositionRef.current.y
            );
          }
        });
      });
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (!containerRef.current) return;
      
      // Don't propagate - we want this to be the only tooltip showing
      e.stopPropagation();
      
      // Clear any pending hide timeout from previous tooltip
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      
      // Mark this as a touch interaction
      isTouchInteractionRef.current = true;
      
      // Find the actual card element (article or the first child of the container)
      const cardElement = containerRef.current.querySelector('article') || containerRef.current.firstElementChild as HTMLElement || containerRef.current;
      
      // Get the bounding box of the card (NOT using touch point position)
      const rect = cardElement.getBoundingClientRect();
      
      // Estimate tooltip width (typical width for the text)
      const estimatedTooltipWidth = tooltipText.length * 8 + 32; // rough estimate: 8px per char + padding
      
      // Center horizontally in the card
      let x = rect.left + (rect.width / 2) - (estimatedTooltipWidth / 2);
      const pageWidth = window.innerWidth;
      const offset = 6;
      
      // Ensure it doesn't overflow edges
      if (x + estimatedTooltipWidth > pageWidth - offset) {
        x = pageWidth - estimatedTooltipWidth - offset;
      }
      if (x < offset) {
        x = offset;
      }
      
      // Center vertically in the card
      const y = rect.top + (rect.height / 2) - 20; // -20 to roughly center the tooltip height
      
      // Set position and make it visible immediately
      setTooltipPosition({ x, y });
      setIsVisible(true);
      
      // Directly set the position on the tooltip element to prevent any layout shifts
      if (tooltipRef.current) {
        tooltipRef.current.style.left = `${x}px`;
        tooltipRef.current.style.top = `${y}px`;
      }
    };

    const handleTouchEnd = () => {
      // Clear any existing timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      
      // Delay hiding to allow the tooltip to be visible
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setTooltipPosition(null);
        isTouchInteractionRef.current = false;
        hideTimeoutRef.current = null;
      }, 2000);
    };

    const handleMouseLeave = () => {
      // Only hide on mouse leave for non-touch devices
      // Touch devices will handle hiding via touchend
      if (!isTouchDevice) {
        setIsVisible(false);
        setTooltipPosition(null);
      }
    };

    const handleScroll = () => {
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      
      // Hide tooltip when scrolling - only for touch devices
      // Non-touch devices should keep tooltip visible during scroll
      if (isVisible && isTouchDevice) {
        setIsVisible(false);
        setTooltipPosition(null);
        isTouchInteractionRef.current = false;
      }
    };

    const handleGlobalTouch = (e: TouchEvent) => {
      // If a touch happens outside this component and we have a visible tooltip, hide it immediately
      if (isVisible && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        setIsVisible(false);
        setTooltipPosition(null);
        isTouchInteractionRef.current = false;
      }
    };

    const container = containerRef.current;
    if (container) {
      // Mouse events (always add for desktop behavior)
      window.addEventListener('mousemove', handleGlobalMouseMove);
      // Track mouse position when over the container for more accurate positioning
      container.addEventListener('mousemove', handleContainerMouseMove as EventListener);
      container.addEventListener('mouseenter', handleMouseEnter as EventListener);
      container.addEventListener('mouseleave', handleMouseLeave);
      
      // Touch events (always add, will be handled based on device capability)
      container.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });
      container.addEventListener('touchend', handleTouchEnd as EventListener, { passive: true });
      
      // Global touch listener to hide tooltip when touching anywhere else
      if (isTouchDevice) {
        window.addEventListener('touchstart', handleGlobalTouch as EventListener, { passive: true, capture: true });
      }
      
      // Scroll event (for all devices)
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        container.removeEventListener('mousemove', handleContainerMouseMove as EventListener);
        container.removeEventListener('mouseenter', handleMouseEnter as EventListener);
        container.removeEventListener('mouseleave', handleMouseLeave);
        container.removeEventListener('touchstart', handleTouchStart as EventListener);
        container.removeEventListener('touchend', handleTouchEnd as EventListener);
        window.removeEventListener('touchstart', handleGlobalTouch as EventListener);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isVisible, isTouchDevice]);

  // Set position before browser paints to prevent flash (skip for touch interactions)
  useLayoutEffect(() => {
    // Don't reposition if this is a touch interaction (position is already set)
    if (isTouchInteractionRef.current) return;
    
    if (tooltipRef.current && tooltipPosition) {
      tooltipRef.current.style.left = `${tooltipPosition.x}px`;
      tooltipRef.current.style.top = `${tooltipPosition.y}px`;
    }
  }, [tooltipPosition]);

  return (
    <>
      <div ref={containerRef} className="relative">
        {children}
      </div>
      {isVisible && tooltipPosition && typeof window !== 'undefined' && createPortal(
        <>
          <style>{`
            @keyframes fadeInTooltip {
              from {
                transform: scale(0);
              }
              to {
                transform: scale(1);
              }
            }
            .tooltip-fade-in {
              animation: fadeInTooltip 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
          `}</style>
          <div
            ref={tooltipRef}
            role="tooltip"
            className={`${!isTouchDevice ? 'tooltip-fade-in' : ''} fixed pointer-events-none px-4 py-2 body-sm border border-black rounded-full shadow-lg z-50 whitespace-nowrap`}
            style={{
              backgroundColor,
              color: textColor
            }}
          >
            {tooltipText}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
